import React, { useState } from 'react';
import { Check, ChevronLeft, ChevronRight, LoaderCircle, MapPin, ShieldCheck } from 'lucide-react';
import { countries, Country } from '../data/countries';
import { PEEKPASS_BRAND, PEEKPASS_DOMAIN } from '../utils/peekPassBrand';

interface CountryGateProps {
  onComplete: (country: Country) => void;
}

type GateStep = 'country' | 'verification';
type VerificationStatus = 'idle' | 'verifying' | 'verified';

const HUMAN_CHECK_KEY = 'peekpass-human-check';
const PERSISTENCE_SECONDS = 60 * 60 * 24 * 180;

const getCookieValue = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const persistHumanCheck = () => {
  window.localStorage.setItem(HUMAN_CHECK_KEY, 'verified');
  document.cookie = `${HUMAN_CHECK_KEY}=verified;max-age=${PERSISTENCE_SECONDS};path=/;SameSite=Lax`;
};

const hasPersistedHumanCheck = () => (
  window.localStorage.getItem(HUMAN_CHECK_KEY) === 'verified' ||
  window.sessionStorage.getItem(HUMAN_CHECK_KEY) === 'verified' ||
  getCookieValue(HUMAN_CHECK_KEY) === 'verified'
);

const CountryGate: React.FC<CountryGateProps> = ({ onComplete }) => {
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [language, setLanguage] = useState(() => window.localStorage.getItem('peekpass-language') || 'es');
  const [step, setStep] = useState<GateStep>('country');
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>(() => (
    hasPersistedHumanCheck() ? 'verified' : 'idle'
  ));
  const selectedCountry = countries.find((country) => country.code === selectedCode);
  const isVerified = verificationStatus === 'verified';

  const handleVerification = () => {
    if (verificationStatus !== 'idle') return;

    setVerificationStatus('verifying');
    window.setTimeout(() => {
      window.sessionStorage.setItem(HUMAN_CHECK_KEY, 'verified');
      persistHumanCheck();
      setVerificationStatus('verified');
    }, 1000);
  };

  const handleContinue = () => {
    if (selectedCountry && isVerified) {
      persistHumanCheck();
      window.localStorage.setItem('peekpass-language', language);
      document.cookie = `peekpass-language=${language};max-age=${60 * 60 * 24 * 180};path=/;SameSite=Lax`;
      onComplete(selectedCountry);
    }
  };

  return (
    <main className="country-gate-scroll relative min-h-[100svh] w-full overflow-x-hidden bg-[#f7f3ea] px-3 pb-[calc(7rem+env(safe-area-inset-bottom))] pt-3 text-slate-950 sm:px-6 sm:py-6 lg:flex lg:items-center lg:px-10">
      <div className="pointer-events-none absolute -left-32 top-[-10rem] hidden h-[32rem] w-[32rem] rounded-full bg-[#dff38c]/60 blur-3xl sm:block" />
      <div className="pointer-events-none absolute -bottom-52 right-[-8rem] hidden h-[34rem] w-[34rem] rounded-full bg-[#f4cc9e]/45 blur-3xl sm:block" />

      <div className="relative mx-auto grid w-full min-w-0 max-w-[1240px] overflow-hidden rounded-[1.35rem] border border-black/10 bg-white shadow-[0_18px_45px_-34px_rgba(15,23,42,0.34)] sm:rounded-[2rem] sm:shadow-[0_35px_100px_-45px_rgba(15,23,42,0.4)] lg:grid-cols-[0.9fr_1.1fr]">
        <section className="relative hidden min-h-[650px] overflow-hidden bg-black p-10 text-white lg:flex lg:flex-col">
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)', backgroundSize: '22px 22px' }} />
          <div className="relative">
            <div className="h-24 w-[360px] max-w-full overflow-hidden rounded-2xl">
              <img src="/peekpassclubverde_headerlogo.png" alt={PEEKPASS_BRAND} className="h-full w-full object-contain object-left" />
            </div>
            <p className="mt-12 max-w-sm text-4xl font-black leading-[1.02] tracking-[-0.07em]">Los grandes planes empiezan cerca tuyo.</p>
            <p className="mt-5 max-w-sm text-base leading-7 text-white/65">Elegí un país, encontrá tu próxima fecha y viví PEEK PASS desde el primer paso.</p>
          </div>
          <div className="relative mt-auto border-t border-white/15 pt-6">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#dff38c]">PEEK PASS CLUB</p>
            <p className="mt-2 max-w-sm text-sm leading-6 text-white/65">Beneficios, preventas y experiencias para quienes vuelven a salir.</p>
          </div>
        </section>

        <section className="min-w-0 p-4 sm:p-10 lg:p-12">
          <div className="flex min-w-0 items-center justify-between gap-2 lg:hidden">
            <div className="h-14 w-[min(61vw,210px)] min-w-0 overflow-hidden rounded-xl sm:h-16 sm:w-[230px]">
              <img src="/peekpassclubverde_headerlogo.png" alt={PEEKPASS_BRAND} className="h-full w-full object-contain object-left" />
            </div>
            <span className="hidden shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 min-[390px]:block sm:text-xs sm:tracking-[0.2em]">{PEEKPASS_DOMAIN}</span>
          </div>

          <div className="mt-6 sm:mt-9 lg:mt-0">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600">
              <MapPin className="h-3.5 w-3.5" /> Configuración regional
            </span>
            <h1 className="mt-4 text-2xl font-black tracking-[-0.06em] text-slate-950 sm:mt-5 sm:text-4xl lg:whitespace-nowrap">¿Desde dónde nos visitás?</h1>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600 sm:mt-3 sm:text-base sm:leading-7">Elegí el país donde querés descubrir, comprar o publicar eventos.</p>
            <div className="mt-4 flex min-w-0 flex-wrap items-center gap-2 sm:mt-5">
              <span className="mr-1 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Idioma</span>
              <button type="button" onClick={() => setLanguage('es')} className={`border px-3 py-1.5 text-xs font-bold transition ${language === 'es' ? 'border-black bg-black text-white' : 'border-slate-200 bg-white text-slate-600'}`}>Español</button>
              <button type="button" onClick={() => setLanguage('en')} className={`border px-3 py-1.5 text-xs font-bold transition ${language === 'en' ? 'border-black bg-black text-white' : 'border-slate-200 bg-white text-slate-600'}`}>English</button>
            </div>
          </div>

          {step === 'country' ? (
            <>
              <div className="mt-6 grid min-w-0 grid-cols-1 gap-1.5 min-[350px]:grid-cols-2 sm:mt-8 sm:grid-cols-3 sm:gap-2">
                {countries.map((country) => {
                  const isSelected = selectedCode === country.code;
                  return (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => setSelectedCode(country.code)}
                      aria-pressed={isSelected}
                      className={`flex min-w-0 touch-manipulation items-center gap-2 rounded-xl border px-2.5 py-2 text-left text-xs font-semibold transition sm:gap-3 sm:rounded-2xl sm:px-3 sm:py-3 sm:text-sm ${isSelected ? 'border-black bg-black text-white shadow-lg shadow-black/15' : 'border-slate-200 bg-[#fcfbf8] text-slate-700 hover:border-slate-400 hover:bg-white'}`}
                    >
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm sm:h-8 sm:w-8 sm:text-base ${isSelected ? 'bg-[#dff38c] text-black' : 'bg-slate-200 text-slate-700'}`} aria-hidden="true">{country.flag}</span>
                      <span className="min-w-0 truncate">{country.name}</span>
                      {isSelected ? <Check className="ml-auto h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" /> : null}
                    </button>
                  );
                })}
              </div>
              <button type="button" onClick={() => setStep('verification')} disabled={!selectedCountry} className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-50 inline-flex w-auto touch-manipulation items-center justify-center gap-2 rounded-xl border border-white/15 bg-black px-4 py-3.5 text-sm font-bold text-white shadow-[0_18px_45px_-18px_rgba(0,0,0,0.65)] transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 sm:static sm:mt-7 sm:w-full sm:rounded-2xl sm:border-0 sm:px-5 sm:py-4 sm:shadow-none">
                Siguiente <ChevronRight className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <div className="mt-6 rounded-[1.25rem] border border-slate-200 bg-[#fcfbf8] p-4 sm:mt-8 sm:rounded-[1.5rem] sm:p-5">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Paso 2 de 2</p>
                <p className="mt-2 text-lg font-black">Verificación de acceso</p>
                <p className="mt-1 text-sm leading-6 text-slate-600">Confirmá el control para abrir la agenda de {selectedCountry?.name}.</p>
              </div>
              <button
                type="button"
                onClick={handleVerification}
                disabled={verificationStatus !== 'idle'}
                aria-pressed={isVerified}
                className={`mt-4 flex w-full min-w-0 items-center gap-3 rounded-xl border p-3 text-left transition sm:mt-5 sm:gap-4 sm:rounded-2xl sm:p-4 ${isVerified ? 'border-[#b7d957] bg-[#f4fbd9]' : 'border-slate-200 bg-white hover:border-slate-400'} disabled:cursor-wait`}
              >
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-2 ${isVerified ? 'border-black bg-black text-white' : 'border-slate-300 bg-white'}`}>
                  {verificationStatus === 'verifying' ? <LoaderCircle className="h-4 w-4 animate-spin text-slate-700" /> : isVerified ? <Check className="h-4 w-4" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold text-slate-950">{verificationStatus === 'verifying' ? 'Verificando acceso…' : isVerified ? 'Verificación completada' : 'No soy un robot'}</span>
                  <span className="mt-0.5 block text-xs text-slate-500">{verificationStatus === 'verifying' ? 'Estamos validando la solicitud. Esperá un momento.' : isVerified ? 'Ya podés continuar a la agenda.' : 'Tocá para iniciar la verificación.'}</span>
                </span>
                <ShieldCheck className={`h-6 w-6 ${isVerified ? 'text-black' : 'text-slate-400'}`} />
              </button>
              <div className="mt-4 grid gap-2 min-[380px]:grid-cols-2 sm:mt-5 sm:gap-3">
                <button type="button" onClick={() => { setStep('country'); setVerificationStatus(hasPersistedHumanCheck() ? 'verified' : 'idle'); }} className="inline-flex min-w-0 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-700 transition hover:border-black sm:rounded-2xl sm:px-5 sm:py-4"><ChevronLeft className="h-4 w-4 shrink-0" />Cambiar país</button>
                <button type="button" onClick={handleContinue} disabled={!isVerified} className="inline-flex min-w-0 items-center justify-center gap-2 rounded-xl bg-black px-3 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 sm:rounded-2xl sm:px-5 sm:py-4">Ver eventos <ChevronRight className="h-4 w-4 shrink-0" /></button>
              </div>
            </>
          )}
          <p className="mt-5 text-center text-xs leading-5 text-slate-500">{step === 'country' ? 'Vas a confirmar tu acceso antes de abrir la agenda.' : 'Podés volver y cambiar el país antes de continuar.'}</p>
        </section>
      </div>
    </main>
  );
};

export default CountryGate;
